import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { canPlaySlotWith, startablePositions, type SlotEligibilityRow } from '@/lib/nflStartablePositions';
import { leagueRosterability, type LeagueRosterability } from '@/lib/nflRosterability';

/**
 * SAB-955: the positions this league can START, read from the same table the writer uses.
 *
 * `nfl_slot_eligibility` is 27 rows and league-independent, so this is one small read per
 * mount rather than anything that needs caching infrastructure.
 *
 * Returns `null` until the rows land, AND on any read failure. Both mean "do not filter":
 * a failed eligibility read must never empty the draft pool. The cost of being briefly
 * permissive is that a manager could click a player the writer then refuses with a clear
 * error; the cost of being wrongly restrictive is a pool with no players in it during a
 * live draft on a clock.
 */

/**
 * The raw read, shared by both hooks below.
 *
 * Extracted in SAB-998 so a surface wanting the FLEX set does not issue a SECOND copy of
 * the same query alongside a surface wanting startability. Same read, same null-on-failure
 * contract, one place that talks to the table.
 */
function useSlotEligibilityRows(): SlotEligibilityRow[] | null {
  const [rows, setRows] = useState<SlotEligibilityRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('nfl_slot_eligibility' as never)
        .select('position, slot_base');
      if (cancelled) return;
      if (error || !data || (data as unknown[]).length === 0) {
        // Deliberately stays null -- see the header. Logged so a persistent failure is
        // visible rather than silently degrading to an unfiltered pool forever.
        // Empty counts as failed: nfl_slot_eligibility is a seeded reference table that
        // is never legitimately empty, and believing [] would filter every player out of
        // the pool. See startablePositions.
        console.error('[startable-positions] eligibility read failed or empty:', error?.message);
        return;
      }
      setRows(data as unknown as SlotEligibilityRow[]);
    })();
    return () => { cancelled = true; };
  }, []);

  return rows;
}

export function useStartablePositions(rosterSlots: unknown): Set<string> | null {
  const rows = useSlotEligibilityRows();
  return useMemo(() => startablePositions(rows, rosterSlots), [rows, rosterSlots]);
}

/**
 * SAB-998: the full scoping — startability, FLEX presence, and the FLEX position set.
 *
 * Superset of `useStartablePositions`; that hook stays because the draft room only needs
 * the one field and there is no reason to make it destructure. Both read through
 * `useSlotEligibilityRows`, so mounting both costs one query, not two.
 */
export function useLeagueRosterability(rosterSlots: unknown): LeagueRosterability {
  const rows = useSlotEligibilityRows();
  return useMemo(() => leagueRosterability(rows, rosterSlots), [rows, rosterSlots]);
}

/**
 * SAB-812: `(position, slot) => boolean`, derived from the table rather than from a copy.
 *
 * Returns a STABLE function per rows value, so consumers can list it in a useMemo
 * dependency array and have their memo recompute when the rows land. That is not a
 * nicety: while the rows are unloaded `canPlaySlotWith` answers permissively, so a memo
 * that captured the pre-load function and never re-ran would keep offering slots the
 * player cannot fill, for the life of the mount.
 */
export function useCanPlaySlot(): (position: string, slot: string) => boolean {
  const rows = useSlotEligibilityRows();
  return useMemo(
    () => (position: string, slot: string) => canPlaySlotWith(rows, position, slot),
    [rows],
  );
}
